import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, X, AlertTriangle } from "lucide-react";
import { OFF_PIPELINE } from "@/lib/vehicleLists";
import { truckParams } from "@/components/search/engine";
import { TapChips } from "@/components/search/TapChips";

// The drawer's copy changes the way the page's address does: a blank, "all" or false drops the filter (S5).
const KEEP = ["q", "order"];
function stagePatch(d, patch) {
  const n = { ...d };
  Object.entries(patch).forEach(([k, val]) => {
    if (val == null || val === "" || val === false || (val === "all" && k !== "q")) delete n[k];
    else n[k] = val === true ? "1" : String(val);
  });
  delete n.page;
  return n;
}

// The draft's counts and total, asked for once the draft differs from the page — no search is recorded (no box).
function useDraftCounts(draft, dirty) {
  const [got, setGot] = useState(null);   // {draft, data} — the counts and WHICH copy of the drawer they were for
  const seq = useRef(0);
  useEffect(() => {
    const my = ++seq.current;
    if (!draft || !dirty) { setGot(null); return undefined; }
    const t = setTimeout(() => {
      const p = { ...truckParams((k) => draft[k]), page_size: 1 };
      delete p.page; delete p.order;
      // the Vehicles list hides scrapped trucks unless "show scrapped" is on — the drawer's count says the same
      p.gone = draft.gone === "1" ? "1" : "0";
      if ((draft.q || "").trim()) p.q = draft.q;
      api.get("/truck-search", { params: p })
        .then(({ data }) => { if (my === seq.current) setGot({ draft, data }); })
        .catch(() => { if (my === seq.current) setGot((g) => ({ draft, data: g?.data, failed: true })); });
    }, 250);
    return () => clearTimeout(t);
  }, [draft, dirty]);
  // the counts stay on the options while a new one is on its way; the TOTAL is shown only for exactly this copy
  return [got?.data || null, !(got && got.draft === draft && !got.failed)];
}

// THE VIN CHECK, AS A WORKLIST (his ask, 2026-10-07: "put both lists where I can work through
// them — a filter on the Vehicles page would do it"). The free check already flagged one truck at
// a time on its own page; what it could not do was hand him the fourteen to take to the plates.
//
// THE THREE SHORT-VIN CASES ARE NAMED APART, because only one of them is work: a pre-1981 truck
// legitimately has a shorter VIN; a truck already RECORDED as VIN-unknown is a plate somebody
// found unreadable and typed as much of as they could (PRODUCTION: all 27 of his short VINs are
// this case); and a modern truck carrying half a VIN that nobody has said anything about is the
// one worth chasing. A flag NEVER changes a VIN — he corrects them himself.
export const VIN_FLAG_LABELS = {
  any: "Any VIN flag",
  check_digit: "VIN check digit doesn't match",
  partial: "Partial VIN — nobody said why",
  vin_unknown: "Partial VIN — recorded as VIN-unknown",
  pre_1981: "Short VIN, pre-1981 — normal",
  forbidden_chars: "VIN contains I, O or Q",
  length: "VIN isn't 17 characters",
};

// THE DECODE CHECK, THE SECOND WORKLIST, AND IT SITS BESIDE THE FIRST (his ruling, 2026-10-09:
// "Put it in the existing Filters panel, beside the VIN worklist — 'Engine not checked (220)' …
// One worklist area, no new clutter. The count goes down as people check them.")
//
// WHY IT EXISTS: a VIN decode wrote an engine onto a truck, nobody checked it, and the record had
// no way of saying so — production carried provenance on NOT ONE of 287 trucks. An engine nobody
// has put their name to is a fact we would sell as true, which is why it is work and not a note.
export const DECODE_FLAG_LABELS = {
  engine_unchecked: "Engine not checked by a person (or none entered)",
  any_unchecked: "Any decoded detail not checked",
  corrected: "Corrected by a person",
};

// "Nobody recorded one" is a value you can pick, not a blank you have to notice — the same rule
// the uncategorized filter already follows. Shared with the server so the two can't drift.
export const NOT_RECORDED = "__none__";
// S5 "date arrived" — the day the truck was put in the system; quick taps only (his call). Same codes as the server.
export const ARRIVED_LABELS = { "7d": "last 7 days", "30d": "last 30 days", "90d": "last 90 days", year: "this year" };

// ALL the narrowing controls behind ONE button, with what's currently on shown as chips.
//
// They used to sit in a single row: seven dropdowns plus a toggle plus a clear link, ten standing
// controls before you reached a truck. Every one was useful; together they read as a control panel.
// The options are unchanged — the COUNTS moved out to the health band, because a number belongs in
// exactly one place and a tile you can click beats a number hidden in a dropdown's option text.
export function TruckFilterPanel({
  params, stageCodes, stageLabel, stages, fleetTotal, facets: facets0, vinFacets: vinFacets0, decodeFacets: decodeFacets0,
  dataplateFacets: dataplateFacets0, tagFacets, vehicleFacets: vehicleFacets0, truckTypes, categories,
  offPipeline, canPrintTags, activeCount, seedUp, onParam, onClear, total, children,
}) {
  const [open, setOpen] = useState(false);
  // S5 — THE DRAWER STAGES (his yes): a pick changes only the drawer's own copy, its counts are asked for that copy,
  // and the list moves only on "Show results". Closing it any other way drops the copy.
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [live, counting] = useDraftCounts(draft, dirty);
  const openPanel = (o) => { setDraft(o ? { ...params } : null); setDirty(false); setOpen(o); };
  const stage = (patch) => { setDraft((d) => stagePatch(d || params, patch)); setDirty(true); };
  const apply = () => {
    const keys = new Set([...Object.keys(params), ...Object.keys(draft || {})].filter((k) => !KEEP.includes(k) && k !== "page"));
    onParam(Object.fromEntries([...keys].map((k) => [k, (draft || {})[k] ?? ""])));
    openPanel(false);
  };
  const clearDraft = () => { setDraft((d) => Object.fromEntries(KEEP.filter((k) => (d || {})[k]).map((k) => [k, d[k]]))); setDirty(true); };
  const cur = draft || params;
  const facets = (live && live.stage_facets) || facets0;
  const vinFacets = (live && live.vin_facets) || vinFacets0;
  const decodeFacets = (live && live.decode_facets) || decodeFacets0;
  const dataplateFacets = (live && live.dataplate_facets) || dataplateFacets0;
  const vehicleFacets = (live && live.vehicle_facets) || vehicleFacets0;
  const shownTotal = dirty ? (counting ? undefined : live?.total) : total;
  // CONDITION IS FIRST-CLASS SEARCHABLE — "known bad engines", "engines nobody has looked at",
  // "500 HP engines that aren't known bad" (with a spec filter on top). The vocabulary and the
  // component list come from the server so this can never drift from what the truck page shows.
  const [cond, setCond] = useState({ states: [], groups: [] });
  const [sectors, setSectors] = useState([]);
  // the sector list is the SAME list the placement picker uses, so the filter and the action can
  // never offer different places. Loaded on mount, not on open, because the active-filter CHIP
  // names the sector in words and a chip must never sit there showing a bare code.
  useEffect(() => {
    api.get("/donors/place-sectors").then(({ data }) => setSectors(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!open || cond.groups.length) return;
    Promise.all([api.get("/condition-vocabulary"), api.get("/component-groups")])
      .then(([v, g]) => setCond({ states: v.data.states || [], groups: g.data.items || [] }))
      .catch(() => {});
  }, [open, cond.groups.length]);
  const condComp = (cur.condition || "").split(":")[0] || "";
  const condState = (cur.condition || "").split(":")[1] || "";
  const setCondition = (comp, state) => stage({
    condition: comp && state ? `${comp}:${state}` : "", page: 1 });
  const v = (k, dflt = "all") => cur[k] || dflt;
  // THE STAGE COUNTS DESCRIBE THE SET YOU ARE CURRENTLY LOOKING AT (owner ruling 2026-09-24).
  // They arrive with each search response, scoped to every OTHER filter that is on, so they move
  // live as you narrow and each option's count is exactly what picking it returns. The fleet-wide
  // KPI figures are the fallback for the first paint, before a response has landed.
  const fStages = facets?.stages?.length ? facets.stages : (stages || []);
  const fOff = facets?.off_pipeline || offPipeline;
  const fTotal = typeof facets?.total === "number" ? facets.total : fleetTotal;
  const stageSum = fStages.length
    ? fStages.reduce((n, s) => n + (s.count || 0), 0) + (fOff?.count || 0)
    : null;

  const chipsFor = (p) => {
  const pv = (k, dflt = "all") => p[k] || dflt;
  const chips = [];
  const chip = (key, label, reset) => chips.push({ key, label, reset });
  if ((p.q || "").trim()) chip("q", `“${p.q.trim()}”`, { q: "" });
  // THE VEHICLE CHIPS READ LIKE HE SAYS IT: "Freightliner · Cascadia · DD15 · 2015–2019".
  const facetLabel = (list, val) => ((list || []).find((o) => o.value === val)?.label) || val;
  if (pv("make", "")) {
    chip("make", pv("make") === NOT_RECORDED ? "No make recorded"
      : facetLabel(vehicleFacets?.makes, pv("make")), { make: "", model: "" });
  }
  if (pv("model", "")) {
    chip("model", pv("model") === NOT_RECORDED ? "No model recorded"
      : facetLabel(vehicleFacets?.models, pv("model")), { model: "" });
  }
  if (pv("engine", "")) {
    chip("engine", pv("engine") === NOT_RECORDED ? "No engine recorded"
      : `Engine: ${facetLabel(vehicleFacets?.engines, pv("engine"))}`, { engine: "" });
  }
  if (pv("year_from", "") || pv("year_to", "")) {
    const yf = pv("year_from", ""), yt = pv("year_to", "");
    chip("year", yf && yt ? (yf === yt ? yf : `${yf}–${yt}`) : yf ? `${yf} and newer` : `${yt} and older`,
      { year_from: "", year_to: "" });
  }
  if (pv("status") !== "all") {
    chip("status", pv("status") === OFF_PIPELINE ? "Not in any stage" : stageLabel(pv("status")), { status: "all" });
  }
  if (pv("truck_type") !== "all") chip("truck_type", pv("truck_type") === NOT_RECORDED ? "No type yet" : pv("truck_type"), { truck_type: "all" });
  if (pv("category") !== "all") {
    const c = (categories || []).find((x) => x.code === pv("category"));
    chip("category", c?.name || pv("category"), { category: "all" });
  }
  if (pv("placement") !== "all") chip("placement", { pending: "No location", holding: "In a holding area",
    no_real_place: "No real place" }[pv("placement")] || "Placed somewhere", { placement: "all" });
  if (pv("sector") && pv("sector") !== "all") {
    const s = (sectors || []).find((x) => x.code === pv("sector"));
    chip("sector", `In ${s ? (s.name && s.name !== s.code ? `${s.name} (${s.code})` : s.code) : pv("sector")}`, { sector: "all" });
  }
  if (pv("tag_print") !== "all") chip("tag_print", pv("tag_print") === "stale" ? "Tag out of date" : `Tag: ${pv("tag_print")}`, { tag_print: "all" });
  if (pv("sheet", "") ) chip("sheet", { none: "No build sheet", missing: "No build sheet, none marked unavailable", attached: "Sheet waiting", exact: "Sheet read exactly", scrubbed: "Sheet read by the AI", thin: "Sheet said almost nothing", landed_nothing: "Nothing landed off the sheet", failed: "Sheet read failed", has: "Has a build sheet" }[pv("sheet", "")] || pv("sheet", ""), { sheet: "" });
  if (pv("photos", "")) chip("photos", { unread: "Photos waiting to be read", none: "No photo read at all", read: "Every photo read", incomplete: "Some photos still to read", stale: "Read, but new information available", no_photos: "No photos on the truck" }[pv("photos", "")] || pv("photos", ""), { photos: "" });
  if (pv("capture", "")) chip("capture", pv("capture", "") === "none" ? "No specs recorded" : "Barely recorded", { capture: "", sheet: "" });
  if (pv("vin_flag", "")) chip("vin_flag", VIN_FLAG_LABELS[pv("vin_flag", "")] || pv("vin_flag", ""), { vin_flag: "" });
  if (pv("decode_flag", "")) chip("decode_flag", DECODE_FLAG_LABELS[pv("decode_flag", "")] || pv("decode_flag", ""), { decode_flag: "" });
  if (p.condition) {
    const [cc, cs] = [(p.condition || "").split(":")[0] || "", (p.condition || "").split(":")[1] || ""];
    const g = (cond.groups || []).find((x) => x.code === cc);
    const st = (cond.states || []).find((x) => x.code === cs);
    const stTxt = st?.short || { not_assessed: "NEVER ASSESSED", any_assessed: "ASSESSED, EITHER WAY", sellable: "CAN BE SOLD", not_bad: "CAN BE SOLD" }[cs] || cs;
    chip("condition", `${g?.name || cc === "any" ? (cc === "any" ? "Any component" : g?.name) : cc}: ${stTxt}`, { condition: "" });
  }
  if (p.needs_info) chip("needs_info", "Needs info", { needs_info: false });
  if (p.truck_filters) chip("truck_filters", "Spec filters", { truck_filters: "" });
  if (pv("transmission", "")) {
    chip("transmission", pv("transmission") === NOT_RECORDED ? "No transmission recorded"
      : `Transmission: ${facetLabel(vehicleFacets?.transmissions, pv("transmission"))}`, { transmission: "" });
  }
  if (pv("arrived", "")) chip("arrived", `Arrived: ${ARRIVED_LABELS[pv("arrived")] || pv("arrived")}`, { arrived: "" });
  return chips;
  };
  // the chips under the button are what is ON the list; the drawer's sentence names the drawer's copy — the words
  // the stage arithmetic uses to NAME the set it describes, every narrowing EXCEPT stage (the thing being chosen)
  const chips = chipsFor(params);
  const scopeChips = chipsFor(cur).filter((c) => c.key !== "status");

  return (
    <>
      <Sheet open={open} onOpenChange={openPanel}>
        <SheetTrigger asChild>
          <Button data-testid="donor-filters-open" variant="outline"
            className={`h-9 rounded-lg ${activeCount ? "border-[#E0781A] text-[#C76714]" : "border-slate-300"}`}>
            <SlidersHorizontal className="w-4 h-4 mr-1.5" />Filters
            {activeCount > 0 && <span data-testid="donor-filters-active-count" className="ml-1.5 rounded-full bg-[#E0781A] px-1.5 text-[10px] font-bold text-white">{activeCount}</span>}
          </Button>
        </SheetTrigger>
        <SheetContent side="right" data-testid="donor-filter-panel" className="w-[92vw] sm:max-w-md overflow-y-auto p-5 pb-0">
          <SheetHeader>
            <SheetTitle className="font-display">Narrow the list</SheetTitle>
            <SheetDescription className="text-xs text-slate-500">
              Every way to narrow the list, in one place. Counts beside an option tell you what
              you'll get before you pick it.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {/* THE VEHICLE ITSELF — year range, make, model, engine (his ask, 2026-10-13). It sits
                FIRST because it is how a person asks for a truck out loud: "the 2018 Cascadias",
                "anything with a DD15". Every value is a CLICK off his own fleet with its count
                beside it — there is nothing to type and nothing to spell, and an option can never
                offer a make he doesn't have. "Not recorded" is an option too, because a truck
                whose make nobody wrote down is a real job, not a blank to hide. */}
            <div data-testid="donor-vehicle-filters" className="rounded-xl border border-[#E0781A]/30 bg-orange-50/40 p-3 space-y-2.5">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">The vehicle itself</div>

              <Field label="Make">
                <Select value={v("make", "") || "all"}
                  onValueChange={(x) => stage({ make: x === "all" ? "" : x, model: "", page: 1 })}>
                  <SelectTrigger data-testid="donor-make-filter" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="Any make" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-80">
                    <SelectItem value="all" data-testid="make-filter-opt-all">Any make</SelectItem>
                    {(vehicleFacets?.makes || []).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={!o.count && o.value !== cur.make} data-testid={`make-filter-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                    ))}
                    {(vehicleFacets?.blanks?.make || 0) > 0 && (
                      <SelectItem value={NOT_RECORDED} data-testid="make-filter-opt-none">No make recorded ({vehicleFacets.blanks.make})</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>

              {/* The model list is already narrowed to the make you picked — the server scopes it,
                  so it can never offer a Cascadia under Volvo. */}
              <Field label="Model" hint={v("make", "") ? "only this make's models" : null}>
                <Select value={v("model", "") || "all"}
                  onValueChange={(x) => stage({ model: x === "all" ? "" : x, page: 1 })}>
                  <SelectTrigger data-testid="donor-model-filter" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="Any model" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-80">
                    <SelectItem value="all" data-testid="model-filter-opt-all">Any model</SelectItem>
                    {(vehicleFacets?.models || []).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={!o.count && o.value !== cur.model} data-testid={`model-filter-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                    ))}
                    {(vehicleFacets?.blanks?.model || 0) > 0 && (
                      <SelectItem value={NOT_RECORDED} data-testid="model-filter-opt-none">No model recorded ({vehicleFacets.blanks.model})</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>

              {/* ENGINE. One option finds it wherever it was recorded — the engine written on the
                  truck, the Engine Make spec or the Engine Model spec — so the count beside an
                  option is the number of trucks picking it returns, not the number in one field. */}
              <Field label="Engine" hint="however it was recorded">
                <Select value={v("engine", "") || "all"}
                  onValueChange={(x) => stage({ engine: x === "all" ? "" : x, page: 1 })}>
                  <SelectTrigger data-testid="donor-engine-filter" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="Any engine" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-80">
                    <SelectItem value="all" data-testid="engine-filter-opt-all">Any engine</SelectItem>
                    {(vehicleFacets?.engines || []).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={!o.count && o.value !== cur.engine} data-testid={`engine-filter-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                    ))}
                    {(vehicleFacets?.blanks?.engine || 0) > 0 && (
                      <SelectItem value={NOT_RECORDED} data-testid="engine-filter-opt-none">No engine recorded ({vehicleFacets.blanks.engine})</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>

              {/* TRANSMISSION (S5) — its make or its model, both specs; one option per value, each with its count */}
              <Field label="Transmission" hint="make or model, however it was recorded">
                <Select value={v("transmission", "") || "all"}
                  onValueChange={(x) => stage({ transmission: x === "all" ? "" : x, page: 1 })}>
                  <SelectTrigger data-testid="donor-transmission-filter" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="Any transmission" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-80">
                    <SelectItem value="all" data-testid="transmission-filter-opt-all">Any transmission</SelectItem>
                    {(vehicleFacets?.transmissions || []).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={!o.count && o.value !== cur.transmission} data-testid={`transmission-filter-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                    ))}
                    {(vehicleFacets?.blanks?.transmission || 0) > 0 && (
                      <SelectItem value={NOT_RECORDED} data-testid="transmission-filter-opt-none">No transmission recorded ({vehicleFacets.blanks.transmission})</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>

              {/* YEAR RANGE — two pickers, both optional, both lists of years he actually has.
                  Pick only "From" for "2015 and newer"; only "To" for "2015 and older". */}
              <Field label="Year range" hint="either end on its own works">
                <div className="flex items-center gap-2">
                  <Select value={v("year_from", "") || "any"}
                    onValueChange={(x) => stage({ year_from: x === "any" ? "" : x, page: 1 })}>
                    <SelectTrigger data-testid="donor-year-from" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="From" /></SelectTrigger>
                    <SelectContent className="rounded-lg max-h-80">
                      <SelectItem value="any" data-testid="year-from-opt-any">From — any</SelectItem>
                      {(vehicleFacets?.years || []).map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`year-from-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-xs text-slate-400">to</span>
                  <Select value={v("year_to", "") || "any"}
                    onValueChange={(x) => stage({ year_to: x === "any" ? "" : x, page: 1 })}>
                    <SelectTrigger data-testid="donor-year-to" className="rounded-lg border-slate-200 bg-white h-10"><SelectValue placeholder="To" /></SelectTrigger>
                    <SelectContent className="rounded-lg max-h-80">
                      <SelectItem value="any" data-testid="year-to-opt-any">To — any</SelectItem>
                      {(vehicleFacets?.years || []).map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`year-to-opt-${o.value}`}>{o.label} ({o.count})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(vehicleFacets?.blanks?.year || 0) + (vehicleFacets?.unusable?.year || 0) > 0 && (
                  <p data-testid="donor-year-blank-note" className="mt-1.5 text-[10px] text-slate-400">
                    A year range can only include a truck whose year we know
                    {(vehicleFacets?.blanks?.year || 0) > 0 && <> — <b className="text-slate-500">{vehicleFacets.blanks.year}</b> in this set have no year recorded</>}
                    {(vehicleFacets?.unusable?.year || 0) > 0 && <>{(vehicleFacets?.blanks?.year || 0) > 0 ? ", and " : " — "}<b className="text-[#C76714]">{vehicleFacets.unusable.year}</b> have something recorded that isn't a year</>}
                    , so a range leaves them out.
                  </p>
                )}
              </Field>

              {/* DATE ARRIVED (S5, his call: quick taps only) — the day the truck was put in the system */}
              <Field label="Date arrived" hint="put in the system">
                <TapChips testid="donor-arrived" value={v("arrived", "")} onPick={(x) => stage({ arrived: x, page: 1 })}
                  options={vehicleFacets?.arrived || []} />
              </Field>
            </div>

            {/* STAGE LIVES HERE NOW, not as ten pills on the page (owner ruling 2026-09-24): half
                of them read zero and together they were a second wall in front of the search box.
                Nothing was lost — the counts came with it, so you still see what you'll get before
                choosing, and "Not in any stage" sits FIRST because it's the one worth reaching. */}
            <Field label="Dismantle stage">
              <Select value={v("status")} onValueChange={(x) => stage({ status: x, page: 1 })}>
                <SelectTrigger data-testid="donor-status-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="All stages" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="status-filter-opt-all">All stages{typeof fTotal === "number" ? ` (${fTotal})` : ""}</SelectItem>
                  {/* "Not in any stage" sits FIRST because it's the one worth reaching. It stays
                      rendered while it is the picked option even at zero, so the control can
                      never go blank on you. */}
                  {((fOff?.count || 0) > 0 || v("status") === OFF_PIPELINE) && (
                    <SelectItem value={OFF_PIPELINE} data-testid="status-filter-opt-off-pipeline">
                      Not in any stage ({fOff?.count || 0}{seedUp && <b className={fOff?.synthetic > 0 ? "text-[#B01E28]" : "text-slate-400"}> · {fOff?.synthetic || 0} SYNTHETIC</b>})
                    </SelectItem>
                  )}
                  {fStages.map((s) => (
                    <SelectItem key={s.code} value={s.code} disabled={!s.count && s.code !== cur.status} data-testid={`status-filter-opt-${s.code}`}>
                      {s.name} ({s.count}{seedUp && s.count > 0 && <b className={s.synthetic > 0 ? "text-[#B01E28]" : "text-slate-400"}> · {s.synthetic || 0} SYNTHETIC</b>})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* THE ARITHMETIC, DIRECTLY UNDER THE OPTIONS IT EXPLAINS — and it says IN WORDS
                  which set it is describing (owner ruling 2026-09-24). A bare "241" sitting
                  inside a filter panel next to a list of 18 would be its own small lie, so with
                  filters on it names them, and with none on it says "whole fleet" plainly. */}
              {stageSum !== null && (
                <p data-testid="stage-reconciliation" className={`mt-1.5 font-mono text-[10px] leading-relaxed ${stageSum === fTotal ? "text-slate-500" : "text-[#B01E28] font-bold"}`}>
                  {stageSum === fTotal
                    ? <>
                        <b data-testid="stage-reconciliation-scope" className="text-slate-600">
                          {scopeChips.length
                            ? `these filters (${scopeChips.map((c) => c.label).join(" · ")})`
                            : "whole fleet"}
                        </b>
                        {" — "}{fTotal} vehicle{fTotal === 1 ? "" : "s"} ={" "}
                        {[...fStages.filter((s) => s.count).map((s) => `${s.count} ${s.name.toLowerCase()}`),
                          ...((fOff?.count || 0) > 0 ? [`${fOff.count} not in any stage`] : [])].join(" + ") || "0"}
                        {v("status") !== "all" && (
                          <span data-testid="stage-reconciliation-note" className="block text-slate-400">
                            (the stage you picked isn't applied to this line — it's what you're choosing between)
                          </span>
                        )}
                      </>
                    : <>The stages add to {stageSum} but this set is {fTotal} — {Math.abs((fTotal || 0) - stageSum)} vehicle(s) are unaccounted for. Tell the office.</>}
                </p>
              )}
              {/* "NOT IN ANY STAGE" IS TWO DIFFERENT PROBLEMS. A truck nobody staged needs a
                  decision; a truck sitting on a value that isn't in your stage list was created
                  that way and needs the value fixed. One number can't say which, so it doesn't. */}
              {(fOff?.values || []).length > 0 && (
                <p data-testid="stage-off-breakdown" className="mt-1 font-mono text-[10px] leading-relaxed text-[#C76714]">
                  of those {fOff.count} in no stage: {fOff.blank || 0} have no stage set at all
                  {fOff.values.map((o) => `, ${o.count} sit on “${o.value}” — a value that is NOT in your stage list`).join("")}
                </p>
              )}
            </Field>

            <Field label="Type">
              <Select value={v("truck_type")} onValueChange={(x) => stage({ truck_type: x, page: 1 })}>
                <SelectTrigger data-testid="donor-type-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="type-filter-opt-all">All types</SelectItem>
                  {/* "No type yet" (29 Oct: 287 of 301 live trucks) — the list staff work down, counted like the rest */}
                  {((vehicleFacets?.blanks?.truck_type || 0) > 0 || cur.truck_type === NOT_RECORDED) && (
                    <SelectItem value={NOT_RECORDED} data-testid="type-filter-opt-none">No type yet ({vehicleFacets?.blanks?.truck_type || 0})</SelectItem>
                  )}
                  {(truckTypes || []).map((t) => {
                    // S5: each type with its count, worked out with every other filter on
                    const n = (vehicleFacets?.truck_types || []).find((o) => o.value === t.name)?.count;
                    return <SelectItem key={t.id} value={t.name} disabled={!!vehicleFacets && !n && t.name !== cur.truck_type} data-testid={`type-filter-opt-${t.name}`}>{t.name}{vehicleFacets ? ` (${n || 0})` : ""}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </Field>

            {(categories || []).length > 0 && (
              <Field label="Category" hint="also decides which spec filters apply">
                <Select value={v("category")} onValueChange={(x) => stage({ category: x, page: 1 })}>
                  <SelectTrigger data-testid="donor-category-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="All categories" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="all" data-testid="category-filter-opt-all">All categories</SelectItem>
                    {/* The count STAYS here. This is the only place it appears — you're choosing a
                        category, and whether that's 61 trucks or 3 is the thing you need to know
                        before clicking. One fact one place forbids duplicating a number, not
                        having one. While the seed is up EVERY count states its seeded share,
                        including a zero one, so a bare number never has to be interpreted. */}
                    {categories.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`category-filter-opt-${c.code}`}>
                        {c.name}
                        {typeof c.count === "number" && <> ({c.count}{seedUp && c.count > 0 && <b className={c.synthetic > 0 ? "text-[#B01E28]" : "text-slate-400"}> · {c.synthetic || 0} SYNTHETIC</b>})</>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Location">
              <Select value={v("placement")} onValueChange={(x) => stage({ placement: x, page: 1 })}>
                <SelectTrigger data-testid="donor-placement-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any location" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="placement-filter-opt-all">Any location</SelectItem>
                  <SelectItem value="pending" data-testid="placement-filter-opt-pending">No location — nobody can find it</SelectItem>
                  <SelectItem value="placed" data-testid="placement-filter-opt-placed">Placed somewhere</SelectItem>
                  <SelectItem value="holding" data-testid="placement-filter-opt-holding">In a holding area — parked, not put away</SelectItem>
                  <SelectItem value="no_real_place" data-testid="placement-filter-opt-no-real-place">No real place — none, or its place was archived</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* "SHOW ME EVERYTHING IN THE BACK ROW" — a real thing he does while standing in it.
                Same field as the column on the row, one implementation, no second filter. */}
            {sectors.length > 0 && (
              <Field label="In which sector">
                <Select value={v("sector") || "all"} onValueChange={(x) => stage({ sector: x === "all" ? "" : x, page: 1 })}>
                  <SelectTrigger data-testid="donor-sector-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Anywhere" /></SelectTrigger>
                  <SelectContent className="rounded-lg max-h-72">
                    <SelectItem value="all" data-testid="sector-filter-opt-all">Anywhere</SelectItem>
                    {sectors.map((sec) => (
                      <SelectItem key={sec.id} value={sec.code} data-testid={`sector-filter-opt-${sec.code}`}>
                        {sec.code} — {sec.name} ({sec.trucks})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            <Field label="Capture depth">
              <Select value={v("capture", "")} onValueChange={(x) => stage({ capture: x === "all" ? "" : x, sheet: x === "thin" ? "has" : v("sheet", ""), page: 1 })}>
                <SelectTrigger data-testid="donor-capture-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any depth" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="capture-filter-opt-all">Any depth</SelectItem>
                  <SelectItem value="none" data-testid="capture-filter-opt-none">No specs recorded at all</SelectItem>
                  <SelectItem value="thin" data-testid="capture-filter-opt-thin">Barely recorded — sheet on file, almost nothing off it</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* COMPONENT CONDITION. Two pickers, because "which component" and "what shape" are
                two questions — and "never assessed" is one of the answers, since the absence of a
                rating is a real, findable state rather than a blank. */}
            <Field label="Component condition">
              <div className="flex gap-2">
                <Select value={condComp || "none"} onValueChange={(x) => setCondition(x === "none" ? "" : x, x === "none" ? "" : (condState || "present_bad"))}>
                  <SelectTrigger data-testid="donor-condition-component" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any component" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="none" data-testid="cond-comp-opt-none">— don't filter on condition —</SelectItem>
                    <SelectItem value="any" data-testid="cond-comp-opt-any">Any component</SelectItem>
                    {(cond.groups || []).map((g) => (
                      <SelectItem key={g.code} value={g.code} data-testid={`cond-comp-opt-${g.code}`}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={condState || "present_bad"} disabled={!condComp}
                  onValueChange={(x) => setCondition(condComp, x)}>
                  <SelectTrigger data-testid="donor-condition-state" className="rounded-lg border-slate-200 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="present_bad" data-testid="cond-state-opt-present_bad">KNOWN BAD (cores)</SelectItem>
                    <SelectItem value="sellable" data-testid="cond-state-opt-sellable">Can actually be sold — not known bad, not gone</SelectItem>
                    <SelectItem value="present_good" data-testid="cond-state-opt-present_good">GOOD — somebody named had hands on it</SelectItem>
                    <SelectItem value="present_unverified" data-testid="cond-state-opt-present_unverified">Present · not verified</SelectItem>
                    <SelectItem value="cant_tell" data-testid="cond-state-opt-cant_tell">CAN'T TELL — looked, no photo shows it</SelectItem>
                    <SelectItem value="gone" data-testid="cond-state-opt-gone">GONE — not on the truck</SelectItem>
                    <SelectItem value="not_assessed" data-testid="cond-state-opt-not_assessed">Never assessed — nobody has looked</SelectItem>
                    <SelectItem value="any_assessed" data-testid="cond-state-opt-any_assessed">Assessed, either way</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-1.5 text-[10px] leading-relaxed text-slate-400">
                “Can actually be sold” deliberately includes the never-assessed — excluding them
                would quietly hide most of the yard — but those rows carry the badge that says
                nobody has looked. GONE is excluded: an engine that isn't on the truck can't be
                good, bad or a core. A component whose tagged part has since been pulled reads
                GONE on the truck page and in the parts search; this filter only knows what
                somebody recorded.
              </p>
            </Field>

            {/* THE PRESENCE PASS. Separate from condition on purpose: "has anybody said what's
                still on this truck" is a question about the RECORD, not about any one component. */}
            <Field label="Presence check">
              <Select value={v("presence", "")} onValueChange={(x) => stage({ presence: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-presence-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="presence-filter-opt-all">Any</SelectItem>
                  <SelectItem value="unchecked" data-testid="presence-filter-opt-unchecked">Not reviewed — record lists parts, nobody's said they're still on it</SelectItem>
                  <SelectItem value="checked" data-testid="presence-filter-opt-checked">Reviewed — somebody has been through it</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Build sheet">
              <Select value={v("sheet", "")} onValueChange={(x) => stage({ sheet: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-sheet-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any build sheet" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="sheet-filter-opt-all">Any build sheet</SelectItem>
                  <SelectItem value="has" data-testid="sheet-filter-opt-has">▣ Has a sheet on file</SelectItem>
                  <SelectItem value="none" data-testid="sheet-filter-opt-none">○ No build sheet</SelectItem>
                  <SelectItem value="missing" data-testid="sheet-filter-opt-missing">○ No build sheet, and nobody marked "none available"</SelectItem>
                  <SelectItem value="attached" data-testid="sheet-filter-opt-attached">▣ Waiting — nobody has read it</SelectItem>
                  <SelectItem value="exact" data-testid="sheet-filter-opt-exact">✓ Read exactly by its maker's reader (no AI)</SelectItem>
                  <SelectItem value="scrubbed" data-testid="sheet-filter-opt-scrubbed">✓ Read by the AI</SelectItem>
                  <SelectItem value="thin" data-testid="sheet-filter-opt-thin">⚠ Read, but it said almost nothing</SelectItem>
                  <SelectItem value="landed_nothing" data-testid="sheet-filter-opt-landed_nothing">⚠ Read fine — but nothing landed on the truck</SelectItem>
                  <SelectItem value="failed" data-testid="sheet-filter-opt-failed">⚠ Read failed</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Photos">
              <Select value={v("photos", "")} onValueChange={(x) => stage({ photos: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-photos-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any photo state" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="photos-filter-opt-all">Any photo state</SelectItem>
                  <SelectItem value="unread" data-testid="photos-filter-opt-unread">◐ Any photo not read yet</SelectItem>
                  <SelectItem value="read" data-testid="photos-filter-opt-read">✓ Every photo read</SelectItem>
                  <SelectItem value="none" data-testid="photos-filter-opt-none">○ None read at all</SelectItem>
                  <SelectItem value="incomplete" data-testid="photos-filter-opt-incomplete">◐ Some read, some waiting</SelectItem>
                  {/* READ, BUT BY AN OLDER SCAN. His ruling: the backlog must be honest, so a
                      truck whose photos were read before the scan could capture item lists and tag
                      part numbers counts as needing a re-scan — and the crew can tell the two
                      kinds apart without a new screen. */}
                  <SelectItem value="stale" data-testid="photos-filter-opt-stale">↻ Read, new information available</SelectItem>
                  <SelectItem value="no_photos" data-testid="photos-filter-opt-no-photos">– No photos on the truck</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {/* THE VIN CHECK AS A WORKLIST — his ask after the production count came back:
                14 check-digit flags and 27 that aren't 17 characters, out of 270 VIN-bearing
                vehicles. A count in a message is not something you can work through; this is.
                Equipment serials are never in here — the same field holds a serial of any
                length. */}
            <Field label="VIN check" hint="flag only — nothing is ever corrected for you">
              <Select value={v("vin_flag", "") || "all"}
                onValueChange={(x) => stage({ vin_flag: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-vin-flag-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any VIN" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="vin-flag-opt-all">Any VIN</SelectItem>
                  {["any", "check_digit", "partial", "vin_unknown", "pre_1981", "forbidden_chars"].map((k) => (
                    <SelectItem key={k} value={k} data-testid={`vin-flag-opt-${k}`}>
                      {{ any: "⚠ ", check_digit: "⚠ ", partial: "◐ ", vin_unknown: "◐ ", pre_1981: "✓ ", forbidden_chars: "⚠ " }[k]}
                      {VIN_FLAG_LABELS[k]}
                      {typeof vinFacets?.[k] === "number" ? ` (${vinFacets[k]})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[10px] leading-relaxed text-slate-400">
                A VIN's 9th character is arithmetic over the other 16, so a mismatch usually means
                one character was misread off the plate — and a wrong VIN is a truck that VIN
                search cannot find. The other two are listed so they can be ruled OUT of the work:
                VINs were only standardised at 17 characters in 1981, and a truck already recorded
                as VIN-unknown is a plate somebody found unreadable, not a mistake.
              </p>
            </Field>

            {/* THE DECODE CHECK, BESIDE THE VIN ONE — his ruling. The two worklists are the same
                kind of job (take this list to the truck and read the real thing), so they live
                next to each other and neither gets a band across the page. The count in each
                option IS the number of rows picking it returns. */}
            <Field label="Decode check" hint="a VIN decode is a starting point, never the truth">
              <Select value={v("decode_flag", "") || "all"}
                onValueChange={(x) => stage({ decode_flag: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-decode-flag-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any decode state" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="decode-flag-opt-all">Any decode state</SelectItem>
                  {["engine_unchecked", "any_unchecked", "corrected"].map((k) => (
                    <SelectItem key={k} value={k} data-testid={`decode-flag-opt-${k}`}>
                      {{ engine_unchecked: "⚠ ", any_unchecked: "◐ ", corrected: "✎ " }[k]}
                      {DECODE_FLAG_LABELS[k]}
                      {typeof decodeFacets?.[k] === "number" ? ` (${decodeFacets[k]})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[10px] leading-relaxed text-slate-400">
                The engine and the gearbox are what a VIN decode most often gets wrong, and nothing
                used to record which values came from a decode — so a value with no record counts
                as unchecked. Open a truck to correct it or tick <b>That's right</b>; either one
                takes it off this list, and what the decode said is kept on the truck either way.
              </p>
            </Field>

            {/* ENGINE DATAPLATE — the catch-up worklist (his ask, 2026-10-16). "Cummins" here means
                an engine A PERSON has checked, never the VIN decode. The count in each option is
                produced by the same predicate the filter runs, so it IS what picking it returns. */}
            <Field label="Engine dataplate" hint="a Cummins plate read off the maker's PDF">
              <Select value={v("dataplate", "") || "all"}
                onValueChange={(x) => stage({ dataplate: x === "all" ? "" : x, page: 1 })}>
                <SelectTrigger data-testid="donor-dataplate-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any dataplate state" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all" data-testid="dataplate-opt-all">Any dataplate state</SelectItem>
                  <SelectItem value="cummins_none" data-testid="dataplate-opt-cummins_none">
                    ○ Cummins — no dataplate yet
                    {typeof dataplateFacets?.cummins_none === "number" ? ` (${dataplateFacets.cummins_none})` : ""}
                  </SelectItem>
                  <SelectItem value="unverified" data-testid="dataplate-opt-unverified">
                    ◐ Dataplate not yet verified
                    {typeof dataplateFacets?.unverified === "number" ? ` (${dataplateFacets.unverified})` : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[10px] leading-relaxed text-slate-400">
                A Cummins engine serial run through QuickServe gives the CPL #, both configuration
                numbers, the ECM code and the calibration — read off their PDF exactly, no AI. This
                list is the engines that haven't had one loaded yet. A VIN decode never puts a truck
                on it; somebody has to have called the engine a Cummins.
              </p>
            </Field>

            {canPrintTags && (
              <Field label="Tag status">
                <Select value={v("tag_print")} onValueChange={(x) => stage({ tag_print: x, page: 1 })}>
                  <SelectTrigger data-testid="donor-tag-filter" className="rounded-lg border-slate-200 h-9"><SelectValue placeholder="Any tag status" /></SelectTrigger>
                  <SelectContent className="rounded-lg">
                    <SelectItem value="all" data-testid="tag-filter-opt-all">Any tag status</SelectItem>
                    <SelectItem value="unprinted" data-testid="tag-filter-opt-unprinted">🏷️ No tag printed yet</SelectItem>
                    <SelectItem value="confirmed" data-testid="tag-filter-opt-confirmed">✓ Printed (confirmed)</SelectItem>
                    <SelectItem value="legacy" data-testid="tag-filter-opt-legacy">Printed (legacy — unvouched)</SelectItem>
                    {/* HIS ADDITION: a tag printed BEFORE somebody corrected a detail the tag
                        shows (stock number, year, make, model, VIN — the engine is not on it). The
                        sticker on that truck is now a lie, so it is a reprint job. */}
                    <SelectItem value="stale" data-testid="tag-filter-opt-stale">
                      ⚠ Tag out of date — reprint
                      {typeof tagFacets?.stale === "number" ? ` (${tagFacets.stale})` : ""}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}

            <button type="button" data-testid="donor-needs-info-filter"
              onClick={() => stage({ needs_info: !cur.needs_info, page: 1 })}
              className={`w-full h-9 px-3 rounded-lg border text-xs font-semibold inline-flex items-center justify-center gap-1.5 ${cur.needs_info ? "border-[#C76714] bg-yellow-50 text-[#C76714]" : "border-slate-200 text-slate-500"}`}>
              <AlertTriangle className="w-3.5 h-3.5" />Needs info only
            </button>

            {/* SPEC FILTERS — always reachable now. They used to appear only after you had picked a
                category, so fine-tuning by spec was hidden behind a step you had no reason to take. */}
            <div data-testid="donor-spec-filters" className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Spec filters</div>
              {typeof children === "function" ? children({ sp: { get: (k) => cur[k] ?? null }, setF: stage }) : children}
            </div>

            {/* nothing above moves the list until this (S5): Clear all empties the drawer's copy, Show results applies it */}
            <div className="sticky bottom-0 -mx-5 flex gap-2 border-t border-slate-200 bg-white px-5 py-3">
              <Button data-testid="donor-filters-clear-all" variant="outline" className="h-12 rounded-lg border-slate-300"
                onClick={clearDraft}>Clear all</Button>
              <Button data-testid="donor-filters-done" className="flex-1 h-12 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"
                onClick={apply}>
                {typeof shownTotal === "number" ? `Show ${shownTotal.toLocaleString()} ${shownTotal === 1 ? "result" : "results"}` : "Show results"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {chips.length > 0 && (
        <div data-testid="donor-filter-chips" className="flex flex-wrap items-center gap-1.5 w-full">
          {chips.map((c) => (
            <button key={c.key} type="button" data-testid={`donor-chip-${c.key}`} onClick={() => onParam({ ...c.reset, page: 1 })}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-600 hover:border-[#B01E28] hover:text-[#B01E28] transition-colors">
              {c.label}<X className="w-3 h-3" />
            </button>
          ))}
          <button type="button" data-testid="donor-filter-clear" onClick={onClear}
            className="text-[11px] text-slate-500 hover:text-slate-800 underline ml-1">Clear all</button>
        </div>
      )}
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-1">
        {label}{hint && <span className="ml-1.5 normal-case tracking-normal text-slate-400">· {hint}</span>}
      </div>
      {children}
    </div>
  );
}
